<template>
  <Stack direction="col" align="center" justify="center" class="w-full pt-[80px]" gap="2xl">
    <Heading size="md" weight="semi" class="text-center" v-if="props.title">{{ props.title }}</Heading>
    <Slider
      :breakpoints="{
        360: { slidesPerView: 1.2 },
        465: { slidesPerView: 2 },
        768: { slidesPerView: 3 },
        1024: { slidesPerView: 4 },
        1400: { slidesPerView: props.products.items.length > 4 ? 4.5 : 4 },
      }"
      :space-between="24"
      :draggable="true"
      :autoplay="false"
      always-show-arrows
      compact
      :items="props.products.items"
      class="w-full md:w-[90%] !align-start select-none"
      data-test-id="pagination"
    >
      <swiper-slide
        v-for="(relatedProduct, index) in updatedProducts"
        :key="index"
        :data-test-id="`GalleryItem-${index}`"
        class="!mt-none sm:px-none !h-full"
      >
        <RelatedProductCard
          image-loading="lazy"
          :content="{
            image: relatedProduct.image,
            title: relatedProduct.displayName,
            subtitle: relatedProduct.sku,
            hasVat: isVatIncluded,
            quantity: 1,
            url: relatedProduct.url,
            vat: isVatIncluded ? true : false,
            unit: productUnitTranslation(
              `${getCustomAttrValue(relatedProduct, 'unit')}` as ProductDetailsType,
            ) as string,
            priceEach: isVatIncluded
              ? priceSelection(relatedProduct, prices, true)
              : priceSelection(relatedProduct, prices, false),
            offerPriceEach: isVatIncluded
              ? priceSelection(relatedProduct, prices, true)
              : priceSelection(relatedProduct, prices, false),
          }"
          :is-related-product="true"
          :is-multiple-u-s-p="false"
          :branch-selected="selectedBranch ? true : false"
          :signed-in="user ? true : false"
          :user-name="user ? user.profile.first_name : ''"
          :usp="{ availability: 'collect' }"
          product-i-d="test"
          :is-tally="false"
          :is-enquire="false"
          @card-branch-select="handleCardBranchSelect"
          @related-card-sign-in="setLoginTray"
          data-fs="relatedProductCard"
          data-test-id="relatedProductCard"
        />
      </swiper-slide>
    </Slider>
    <Button
      variant="secondary"
      class="uppercase"
      data-test-id="shopAllBtn"
      data-fs="pdpShopAllBtn"
      v-if="cta?.text"
      @click="router.push({ path: cta?.link })"
      >{{ cta?.text }}</Button
    >
  </Stack>
</template>

<script lang="ts" setup>
import { useRouter } from "vue-router";
import { SwiperSlide } from "swiper/vue";
import { IMAGES } from "@/constants/images";
import { productUnitTranslation, type ProductDetailsType } from "~/utils/productUnitTranslation";

const { isVatIncluded, setBranchTray, setLoginTray } = useUIState();

const { user } = useUser();
const { selectedBranch } = useBranches();
const router = useRouter();

const props = defineProps<{
  products: any;
  title?: string;
  prices?: any;
  cta?: {
    text: string;
    link: string;
  };
}>();

const getCustomAttrValue = (product: any, attrName: string) => {
  const attr = product.customAttrs.find((attr: any) => attr.name === attrName);
  return attr ? attr.values[0] : null;
};

const priceSelection = (product: any, prices: any[], vat: boolean) => {
  if (Array.isArray(prices) && prices.length > 0) {
    const productSku = product.itemId ? product.itemId.id : product.productId;
    const foundPrice = prices.find((it: any) => it.product_sku === productSku);

    if (foundPrice) {
      return vat ? foundPrice.price_inc_vat : foundPrice.price; // Directly return the value
    }
  }

  // Fallback to custom attribute if no prices found
  return getCustomAttrValue(product, vat ? "price_inc_vat" : "price"); // Directly return the fallback value
};

const updatedProducts = computed(() => {
  return props.products.items.map((product: any) => {
    return {
      ...product,
      image: product.imageSet?.thumbnail?.link?.href || IMAGES.PLACEHOLDER,
      url: `/product/${getCustomAttrValue(product, "url")?.split("/product/")[1]}`,
      unit: productUnitTranslation(`${getCustomAttrValue(product, "unit")}` as ProductDetailsType),
      priceEach: isVatIncluded ? getCustomAttrValue(product, "price_inc_vat") : getCustomAttrValue(product, "price"),
      offerPriceEach: isVatIncluded
        ? getCustomAttrValue(product, "offer_price_inc_vat")
        : getCustomAttrValue(product, "offer_price"),
    };
  });
});

const handleCardBranchSelect = () => {
  setBranchTray();
};
</script>

<style>
.slider-wrapper {
  padding: 12px;
}
</style>
